import React, { useState } from 'react';
import { format, parse, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CalendarDays } from 'lucide-react';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Input } from './ui/input';

const FORMAT = "yyyy-MM-dd'T'HH:mm";

const parseValue = (value) => {
  if (!value) return null;
  const d = parse(value.slice(0, 16), FORMAT, new Date());
  return isValid(d) ? d : null;
};

/** Date + heure en français. `value` / `onChange` utilisent le format "yyyy-MM-dd'T'HH:mm". */
export const DateTimePicker = ({ value, onChange, testId = 'datetime-picker', className = '' }) => {
  const [open, setOpen] = useState(false);
  const date = parseValue(value);
  const time = date ? format(date, 'HH:mm') : '';

  const commit = (nextDate, nextTime) => {
    if (!nextDate) return;
    const [h, m] = (nextTime || '09:00').split(':');
    const merged = new Date(nextDate);
    merged.setHours(Number(h) || 0, Number(m) || 0, 0, 0);
    onChange?.(format(merged, FORMAT));
  };

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="flex-1 basis-44 min-w-0 justify-start font-normal bg-secondary/50 overflow-hidden"
            data-testid={`${testId}-date-trigger`}
          >
            <CalendarDays className="w-4 h-4 mr-2 shrink-0 text-primary" />
            <span className="truncate">{date ? format(date, 'EEEE d MMMM yyyy', { locale: fr }) : 'Choisir une date'}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            locale={fr}
            weekStartsOn={1}
            selected={date || undefined}
            defaultMonth={date || undefined}
            onSelect={(d) => {
              commit(d, time);
              setOpen(false);
            }}
            data-testid={`${testId}-calendar`}
          />
        </PopoverContent>
      </Popover>
      <Input
        type="time"
        step="300"
        value={time}
        onChange={(e) => commit(date || new Date(), e.target.value)}
        className="basis-28 flex-1 min-w-[6.5rem] bg-secondary/50"
        data-testid={`${testId}-time`}
      />
    </div>
  );
};

export default DateTimePicker;
